import {createContext, type ReactNode, use, useCallback, useState} from 'react'

import {getFieldsForCapability} from '../../components/saved-view-options/get-fields-for-capability'
import {Capability} from '../../components/saved-view-options/view-definition'
import {useViewDefinition} from './ViewDefinitionProvider'

type SetVisibleField = (identifier: string, visible: boolean) => void

const VisibleFieldIdentifiersContext = createContext<ReadonlySet<string> | null>(null)
const SetVisibleFieldContext = createContext<SetVisibleField | null>(null)

/**
 * Owns which of the view's attributable fields are currently shown on a card.
 *
 * Pairs with `ViewDefinitionProvider`, which answers a different question:
 *   - Definition: "what fields *could* appear on a card?" (the schema, fixed per view)
 *   - VisibleFields: "which of those are on *right now*?" (user toggle state)
 *
 * Seeded once from the definition — every attributable field starts visible — and mutated
 * by the view options menu. The card reads both: definition for the candidate list,
 * VisibleFields as a per-identifier gate.
 *
 * Identifiers and the setter are split into separate contexts so consumers that only need
 * the setter don't re-render when identifiers change (enforced by the
 * `no-mixed-stability-context-value` lint rule).
 */
export function VisibleFieldsProvider({children}: {children: ReactNode}) {
  const view = useViewDefinition()
  const [identifiers, setIdentifiers] = useState<ReadonlySet<string>>(
    () => new Set(getFieldsForCapability(view, Capability.ATTRIBUTABLE).map(field => field.identifier)),
  )

  const setVisible = useCallback<SetVisibleField>((identifier, visible) => {
    setIdentifiers(current => {
      if (visible === current.has(identifier)) return current
      const next = new Set(current)
      if (visible) next.add(identifier)
      else next.delete(identifier)
      return next
    })
  }, [])

  return (
    <SetVisibleFieldContext value={setVisible}>
      <VisibleFieldIdentifiersContext value={identifiers}>{children}</VisibleFieldIdentifiersContext>
    </SetVisibleFieldContext>
  )
}

export function useVisibleFieldIdentifiers() {
  const value = use(VisibleFieldIdentifiersContext)
  if (value === null) throw new Error('useVisibleFieldIdentifiers must be used within <VisibleFieldsProvider>')
  return value
}

export function useSetVisibleField() {
  const value = use(SetVisibleFieldContext)
  if (value === null) throw new Error('useSetVisibleField must be used within <VisibleFieldsProvider>')
  return value
}
